import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Heart, Users, Award, TrendingUp, Globe, Shield, CheckCircle2, Lightbulb, Scale, Clock, HeadphonesIcon, FileCheck, ShoppingCart, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-700 to-amber-600 text-white py-32">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About Trademark Upgrades
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 leading-relaxed mb-8">
              Your trusted partner in protecting what matters most—your brand identity and intellectual property.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-1">10,000+</div>
                <div className="text-orange-100">Trademarks Protected</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-1">98%</div>
                <div className="text-orange-100">Success Rate</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-1">50+</div>
                <div className="text-orange-100">Expert Team Members</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">Who We Are</h2>
            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                Trademark Upgrades is a premier intellectual property services firm specializing in trademark registration, copyright protection, and brand development. Our team of experienced attorneys and IP specialists is dedicated to helping businesses of all sizes protect their most valuable assets—their brands.
              </p>
              <p>
                We understand that your brand is more than just a name or logo; it's the embodiment of your business's reputation, values, and promise to your customers. That's why we go beyond simple trademark filing—we provide comprehensive brand protection strategies tailored to your unique needs and business goals.
              </p>
              <p>
                Our approach combines deep legal expertise with modern technology and personalized service. We've streamlined the traditionally complex trademark registration process, making it accessible and understandable for entrepreneurs, startups, established corporations, and creative professionals alike.
              </p>
              <p>
                With a proven track record of success and thousands of satisfied clients nationwide, we've established ourselves as a trusted partner in intellectual property protection. Our commitment to excellence, transparency, and client education sets us apart in the industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Enhanced */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Businesses Choose Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver exceptional results through expertise, innovation, and unwavering commitment to client success
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Award className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Proven Expertise</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Our team of specialized attorneys brings extensive knowledge in trademark law, intellectual property protection, and brand strategy.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Clock className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Fast Turnaround</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  We file your application within 24 hours and keep you informed throughout the entire registration process with real-time updates.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <HeadphonesIcon className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">24/7 Support</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  Our dedicated support team is always available to answer your questions and provide guidance whenever you need it.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Transparent Process</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  No hidden fees or surprises. We provide clear pricing upfront and keep you informed at every stage of your trademark journey.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Lightbulb className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Strategic Guidance</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  We don't just file paperwork—we provide strategic advice to maximize your brand protection and business growth.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <FileCheck className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Success Guarantee</h3>
                <p className="text-gray-600 text-center leading-relaxed">
                  With a 98% success rate, we stand behind our work and are committed to achieving the best possible outcomes for your brand.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Foundation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on principles that guide every decision we make
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mb-6 shadow-lg">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  To empower businesses of all sizes with accessible, affordable, and exceptional trademark protection services that secure their brand identity and fuel their growth.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mb-6 shadow-lg">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  To become the nation's most trusted intellectual property partner, recognized for innovation, excellence, and our unwavering commitment to protecting what matters most to our clients.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mb-6 shadow-lg">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                <ul className="text-gray-600 leading-relaxed space-y-3 text-lg">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Integrity in every interaction</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Client success as our priority</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Excellence without compromise</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Innovation in service delivery</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Commitment to You</h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  At Trademark Upgrades, we believe that every business deserves top-tier intellectual property protection, regardless of size or budget. We've built our practice on making professional trademark services accessible to everyone—from solo entrepreneurs launching their first venture to established corporations expanding their brand portfolio.
                </p>
                <p>
                  Our commitment goes beyond just filing paperwork. We take the time to understand your business, your industry, and your long-term goals. This comprehensive approach allows us to provide strategic guidance that protects not just your current brand, but your future growth as well.
                </p>
                <p>
                  We pride ourselves on transparency, clear communication, and educational support. We demystify the trademark process, keeping you informed and empowered throughout your brand protection journey. When you work with us, you're not just getting a service provider—you're gaining a trusted partner invested in your success.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-orange-100 hover:border-orange-500 transition-all">
                <Scale className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Legal Excellence</h3>
                <p className="text-gray-600">Industry-leading expertise in trademark law and IP protection</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-green-100 hover:border-green-500 transition-all">
                <Users className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Client Focus</h3>
                <p className="text-gray-600">Your success is our success—always putting clients first</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-orange-100 hover:border-orange-500 transition-all">
                <Globe className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Nationwide Reach</h3>
                <p className="text-gray-600">Serving businesses across all 50 states</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-orange-100 hover:border-orange-500 transition-all">
                <Shield className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Brand Security</h3>
                <p className="text-gray-600">Comprehensive protection for your most valuable assets</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Approach</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              We combine legal expertise with modern technology and personalized service to deliver exceptional results
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-8 text-white h-full">
                <div className="text-6xl font-bold mb-4 opacity-20">01</div>
                <h3 className="text-2xl font-bold mb-4">Personalized Strategy</h3>
                <p className="text-orange-50 leading-relaxed">
                  Every business is unique. We take the time to understand your specific needs, industry challenges, and brand goals to create a customized protection strategy that works for you.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-500 to-orange-500 rounded-2xl p-8 text-white h-full">
                <div className="text-6xl font-bold mb-4 opacity-20">02</div>
                <h3 className="text-2xl font-bold mb-4">Comprehensive Support</h3>
                <p className="text-orange-50 leading-relaxed">
                  From initial consultation to final registration and beyond, we're with you every step of the way. Our team provides ongoing support, education, and guidance throughout your trademark journey.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-amber-500 rounded-2xl p-8 text-white h-full">
                <div className="text-6xl font-bold mb-4 opacity-20">03</div>
                <h3 className="text-2xl font-bold mb-4">Proactive Protection</h3>
                <p className="text-green-50 leading-relaxed">
                  We don't just react to problems—we help you prevent them. Our proactive approach includes thorough searches, strategic filing, and ongoing monitoring to keep your brand secure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide specialized trademark protection services to businesses across diverse industries
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Technology & Software</h3>
                <p className="text-gray-600 text-sm">Startups, SaaS companies, and tech innovators</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">E-commerce & Retail</h3>
                <p className="text-gray-600 text-sm">Online stores, Amazon sellers, and retail brands</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Professional Services</h3>
                <p className="text-gray-600 text-sm">Consultants, agencies, and service providers</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Creative & Media</h3>
                <p className="text-gray-600 text-sm">Artists, content creators, and media companies</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Health & Wellness</h3>
                <p className="text-gray-600 text-sm">Healthcare providers, fitness, and wellness brands</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Real Estate</h3>
                <p className="text-gray-600 text-sm">Property developers, agencies, and investment firms</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileCheck className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Manufacturing</h3>
                <p className="text-gray-600 text-sm">Product manufacturers and industrial businesses</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">All Industries</h3>
                <p className="text-gray-600 text-sm">From startups to established corporations</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-16 bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl p-8 md:p-12 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl font-bold mb-4">Your Industry, Our Expertise</h3>
              <p className="text-xl text-orange-100 leading-relaxed mb-6">
                No matter what industry you're in, we understand the unique challenges and opportunities you face. Our team has experience working with businesses across every sector, from tech startups to manufacturing giants.
              </p>
              <p className="text-lg text-orange-100 leading-relaxed">
                We take the time to understand your specific industry requirements, competitive landscape, and brand positioning to provide tailored trademark protection strategies that support your business goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-orange-600 via-orange-700 to-amber-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block p-4 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <Shield className="h-16 w-16" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Partner With Us Today
          </h2>
          <p className="text-xl md:text-2xl text-orange-100 mb-12 leading-relaxed max-w-3xl mx-auto">
            Join thousands of successful businesses that trust us with their most valuable asset—their brand. Let's start protecting yours today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto">
              <Link to="/contact">
                Schedule Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto">
              <Link to="/services">
                Explore Our Services
              </Link>
            </Button>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-center">
            <div className="bg-white/10 backdrop-blur-sm px-6 py-4 rounded-lg">
              <div className="text-3xl font-bold mb-1">10,000+</div>
              <div className="text-orange-100">Happy Clients</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-6 py-4 rounded-lg">
              <div className="text-3xl font-bold mb-1">98%</div>
              <div className="text-orange-100">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-6 py-4 rounded-lg">
              <div className="text-3xl font-bold mb-1">24/7</div>
              <div className="text-orange-100">Support Available</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
